import {
  PageSdkErrorReportingContext,
  type PageSdkErrorReporter
} from '@Pages/pageRuntime/compiler/PageSdkErrorReportingContext';
import {
  pageSdkErrorKind,
  pageSdkErrorTelemetryMode,
  reportPageSdkError,
  type PageSdkRenderSurface
} from '@Pages/pageRuntime/compiler/reportPageSdkError';
import { useEffect, useRef, type ReactNode } from 'react';

export function PageSdkErrorReportingProvider({
  children,
  compileFailureScope,
  renderSurface,
  sourcePath
}: {
  children: ReactNode;
  compileFailureScope: unknown;
  renderSurface: PageSdkRenderSurface;
  sourcePath?: string;
}) {
  const compileFailures = useRef({
    scope: compileFailureScope,
    reported: new Set<string>()
  });

  useEffect(() => {
    if (!Object.is(compileFailures.current.scope, compileFailureScope)) {
      compileFailures.current = {
        scope: compileFailureScope,
        reported: new Set<string>()
      };
    }
  }, [compileFailureScope]);

  const reportError: PageSdkErrorReporter = (error, details) => {
    const kind = pageSdkErrorKind(error);

    if (kind === 'compile') {
      if (!Object.is(compileFailures.current.scope, compileFailureScope)) {
        compileFailures.current = {
          scope: compileFailureScope,
          reported: new Set<string>()
        };
      }

      const failureKey = `${details.source}:${error.message}`;

      if (compileFailures.current.reported.has(failureKey)) {
        return;
      }

      compileFailures.current.reported.add(failureKey);
    }

    reportPageSdkError(error, {
      ...details,
      kind,
      telemetryMode: pageSdkErrorTelemetryMode(error, renderSurface),
      renderSurface,
      sourcePath
    });
  };

  return (
    <PageSdkErrorReportingContext.Provider value={reportError}>
      {children}
    </PageSdkErrorReportingContext.Provider>
  );
}
